import {fetch} from './polyfill'
import {reactive, toRefs} from '@vue/composition-api'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const REGIONS = ['Central', 'North', 'South', 'East', 'West']
const SUB_INDICES = ['PM25', 'PM10', 'SO2', 'CO', 'O3', 'NO2']
const PSI_BAND = [
  {low: 0, high: 50, label: 'Good', advisory: [ 'Normal activities', 'Normal activities', 'Normal activities' ]},
  {low: 51, high: 100, label: 'Moderate', advisory: [ 'Normal activities', 'Normal activities', 'Normal activities' ]},
  {low: 101, high: 200, label: 'Unhealthy', advisory: [ 'Reduce prolonged or strenuous outdoor physical exertion', 'Minimise prolonged or strenuous outdoor physical exertion', 'Avoid prolonged or strenuous outdoor physical exertion' ]},
  {low: 201, high: 300, label: 'Very unhealthy', advisory: [ 'Avoid prolonged or strenuous outdoor physical exertion', 'Minimise outdoor activity', 'Avoid outdoor activity' ]},
  {low: 300, high: Infinity, label: 'Hazardous', advisory: [ 'Minimise outdoor activity', 'Avoid outdoor activity', 'Avoid outdoor activity' ]}
]
const PM25_BAND = [
  {low: 0, high: 55, label: 'Band I'},
  {low: 56, high: 150, label: 'Band II'},
  {low: 151, high: 250, label: 'Band III'},
  {low: 251, high: 500, label: 'Band IV'}
]
const TIPS = [
  'When the outdoor air quality appears to be worsening, close doors and windows',
  'Mopping or wiping generally do not produce dust unlike dry-dusting or vacuuming and can be used to remove settled dust',
  'Fans or air-conditioners may be used for air circulation and cooling. Portable air purifiers can help to further reduce the indoor particle level',
  'When PSI hits 101-200, you may want to reduce prolonged or strenuous outdoor activities',
  'When PSI hits 201-300, you may want to avoid prolonged or strenuous outdoor activities',
  'When PSI exceeds 300, you may want to minimise outdoor activities'
]

export function useStore () {
  const store = reactive({
    band: {
      psi: PSI_BAND,
      pm25: PM25_BAND
    },
    regions: REGIONS,
    tips: TIPS,
    psi: null,
    highest: null,
    pm25: null,
    pastMonth: null,
    raw: null,
    lastUpdated: null
  })

  defer(fetchLatest).then(data => {
    store.psi = getPSI(data)
    store.highest = Object.entries(store.psi)
      .reduce((max, curr) => curr[1].value > max[1].value ? curr : max)[0]
    store.pm25 = getPM25(data)
    store.lastUpdated = parseDate(data.ChartAsOfDate)
    store.raw = Object.freeze(data)
  }).catch(() => {
    return fetchDataGov().then(data => {
      store.psi = getPSI2(data)
      store.lastUpdated = Date.parse(data.items[0].timestamp)
    })
  }).then(() => {
    return fetchPastMonth(store.lastUpdated).then(data => {
      store.pastMonth = getPastMonth(data, store)
    })
  }).catch(console.error)

  return toRefs(store)
}

function fetchLatest () {
  const url = 'https://api-graphics.straitstimes.com/psi/production/latest/' + getTimestamp()
  return fetch(url).then(res => {
    if (res.status === 500) throw new Error()
    return res.json()
  })
}

function fetchDataGov () {
  const url = `https://api.data.gov.sg/v1/environment/psi`
  return fetch(url).then(res => res.json())
}

function fetchPastMonth (lastUpdated) {
  const h = (new Date(lastUpdated).getUTCHours() + 8) % 24
  const apiEndpoint = process.env.NODE_ENV === 'production'
    ? 'https://www.straitstimes.com/multimedia/graphics/json/psi/'
    : 'https://s3-st-graphics-json.s3-ap-southeast-1.amazonaws.com/psi/'
  const url = `${apiEndpoint}${h.toFixed().padStart(2, '0')}.json`
  return fetch(url).then(res => res.json())
}

function getPSI (data) {
  const psi = {}
  REGIONS.forEach(region => {
    let max = 0
    SUB_INDICES.forEach(index => {
      const readings = data['Chart' + index][region].Data
        .filter(reading => reading.band != null)
      const lastReading = readings[readings.length - 1]
      if (lastReading.value > max) max = lastReading.value
    })
    const band = PSI_BAND.find(b => max >= b.low && max <= b.high)
    psi[region] = {
      value: max,
      band,
      class: band.label.replace(/ /g, '-').toLowerCase()
    }
  })
  return psi
}

function getPSI2 (data) {
  const latest = data.items[0].readings['psi_twenty_four_hourly']
  const psi = {}
  REGIONS.forEach(region => {
    const value = latest[region.toLowerCase()]
    const band = PSI_BAND.find(b => value >= b.low && value <= b.high)
    psi[region] = {
      value,
      band,
      class: band.label.replace(/ /g, '-').toLowerCase()
    }
  })
  return psi
}

function getPM25 (data) {
  const categories = data.Categories
  const pm25 = {}
  REGIONS.forEach(region => {
    const readings = data['Chart1HRPM25'][region].Data
      .filter(reading => reading.band != null)
    pm25[region] = readings.map((row, i) => {
      const band = PM25_BAND.find(b => row.value >= b.low && row.value <= b.high)
      return {
        label: categories[i],
        value: row.value,
        band,
        class: ['band-' + (PM25_BAND.indexOf(band) + 1), {latest: i === readings.length - 1}]
      }
    })
  })
  return pm25
}

function getPastMonth (data, store) {
  const pastMonth = {}
  REGIONS.forEach(region => {
    const latest = {
      label: store.lastUpdated,
      value: store.psi[region].value,
      class: store.psi[region].class
    }
    const r = region.toLowerCase()
    const past = data.map(row => {
      const value = row[r]
      const band = PSI_BAND.find(b => value >= b.low && value <= b.high)
      return {
        label: row.timestamp,
        value,
        class: band.label.replace(/ /g, '-').toLowerCase()
      }
    })
    pastMonth[region] = [latest, ...past].reverse()
  })
  return pastMonth
}

function parseDate (str) {
  const [timeStr, dateStr] = str.split(' on ')
  const [d, m, y] = dateStr.split(' ')
  const timeMatched = timeStr.match(/(\d+)(am|pm)/)
  const h = (+timeMatched[1]) % 12 + (timeMatched[2] === 'pm' ? 12 : 0) - 8
  return Date.UTC(+y, MONTHS.indexOf(m), +d, h)
}

function getTimestamp () {
  const now = Date.now()
  return Math.floor(now / 1000 / 300) * 300
}

function defer (fn, ...args) {
  return new Promise(resolve => {
    setTimeout(() => resolve(fn(...args)), 0)
  })
}
